import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  MenuItem,
  Grid,
  Select,
  FormControl,
  InputLabel,
  Checkbox,
  ListItemText,
  OutlinedInput
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  FileDownload as ExportIcon
} from '@mui/icons-material';
import { studentAPI } from '../services/api';
import api from '../services/api';
import { toast } from 'react-toastify';
import StudentForm from '../components/StudentForm';
import ExcelImport from '../components/ExcelImport';
import { FileDownload as ImportIcon } from '@mui/icons-material';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

const AVAILABLE_COLUMNS = [
  { id: 'grNo', label: 'GR No.' },
  { id: 'libraryNumber', label: 'Library No.' },
  { id: 'name', label: 'Full Name' },
  { id: 'classDiv', label: 'Class & Div' },
  { id: 'rollNo', label: 'Roll No.' },
  { id: 'dob', label: 'Date of Birth' },
  { id: 'phone', label: 'Phone' },
  { id: 'gender', label: 'Gender' },
  { id: 'uid', label: 'Aadhar/UID' },
  { id: 'religion', label: 'Religion' },
  { id: 'status', label: 'Status' },
];

const Students = () => {
  const [students, setStudents] = useState([]);
  const [_loading, setLoading] = useState(true);
  
  // Filters
  const [search, setSearch] = useState('');
  const [filterClass, setFilterClass] = useState('');
  const [filterSection, setFilterSection] = useState('');
  
  // Column Visibility
  const [visibleColumns, setVisibleColumns] = useState(['grNo', 'name', 'classDiv', 'rollNo', 'dob', 'phone', 'status']);
  
  // Master data
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  
  const [openForm, setOpenForm] = useState(false);
  const [studentToEdit, setStudentToEdit] = useState(null);
  const [openImport, setOpenImport] = useState(false);

  // Teacher specific state
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const [teacherAssignments, setTeacherAssignments] = useState([]);
  const [isClassTeacher, setIsClassTeacher] = useState(false);
  const [classTeacherOf, setClassTeacherOf] = useState(null);

  useEffect(() => {
    fetchClasses();
    if (user?.role === 'teacher') {
      fetchTeacherAssignments();
    }
  }, []);

  const fetchTeacherAssignments = async () => {
    try {
      const response = await api.get('/staff/my-classes');
      if (response.data.success && response.data.data) {
        setTeacherAssignments(response.data.data.assignments || []);
        setIsClassTeacher(response.data.data.isClassTeacher || false);
        setClassTeacherOf(response.data.data.classTeacherOf || null);
      }
    } catch (error) {
      console.error('Failed to fetch teacher assignments', error);
    }
  };

  useEffect(() => {
    fetchStudents();
  }, [filterClass, filterSection]);

  const fetchClasses = async () => {
    try {
      const response = await api.get('/classes');
      setClasses(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch classes');
    }
  };

  const handleClassFilterChange = async (e) => {
    const classId = e.target.value;
    setFilterClass(classId);
    setFilterSection('');
    
    if (classId) {
      try {
        const response = await api.get(`/classes/${classId}/sections`);
        setSections(response.data.data || []);
      } catch (error) {
        console.error('Failed to fetch sections');
      }
    } else {
      setSections([]);
    }
  };

  const handleColumnToggle = (event) => {
    const {
      target: { value },
    } = event;
    setVisibleColumns(
      typeof value === 'string' ? value.split(',') : value,
    );
  };

  const fetchStudents = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterClass) params.class = filterClass;
      if (filterSection) params.section = filterSection;
      
      const response = await studentAPI.getAll(params);
      setStudents(response.data.data);
    } catch (error) {
      toast.error('Error fetching students');
    } finally {
      setLoading(false);
    }
  };

  const exportToExcel = async () => {
    try {
      const params = {};
      if (filterClass) params.classId = filterClass;
      if (filterSection) params.sectionId = filterSection;

      const response = await studentAPI.export(params);
      const data = response.data.data;
      if (!data || data.length === 0) {
        toast.warning("No data to export");
        return;
      }
      
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Students');
      if (data.length > 0) {
          const headers = Object.keys(data[0]);
          worksheet.addRow(headers);
          data.forEach(row => {
              worksheet.addRow(headers.map(h => row[h]));
          });
      }
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(blob, 'Students_Export.xlsx');
      toast.success("Export successful!");
    } catch (error) {
      toast.error("Export failed!");
    }
  };

  const handleOpenForm = (student = null) => {
    setStudentToEdit(student);
    setOpenForm(true);
  };

  const handleCloseForm = () => {
    setOpenForm(false);
    setStudentToEdit(null);
  };

  const handleImportStudents = async (data) => {
    if (!filterClass || !filterSection) {
      toast.error('Please select a Class and Division before importing');
      return;
    }

    // Fetch current academic year first (required field)
    let currentAcademicYearId = 1;
    try {
      const ayRes = await api.get('/academic-years');
      const years = ayRes.data.data || [];
      const current = years.find(y => y.isCurrent) || years[0];
      if (current) currentAcademicYearId = current.id;
    } catch(e) {
      console.warn('Could not fetch academic years, using default');
    }

    // Helper: Convert Excel serial date number to YYYY-MM-DD string
    const excelDateToString = (val) => {
      if (!val) return null;
      // If already a date string like "2010-01-01", return as-is
      if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
      // DD-MM-YYYY format -> convert to YYYY-MM-DD
      if (typeof val === 'string' && /^\d{2}-\d{2}-\d{4}$/.test(val)) {
        const parts = val.split('-');
        return `${parts[2]}-${parts[1]}-${parts[0]}`;
      }
      // DD/MM/YYYY format -> convert to YYYY-MM-DD  
      if (typeof val === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(val)) {
        const parts = val.split('/');
        return `${parts[2]}-${parts[1]}-${parts[0]}`;
      }
      // If it contains a dash but doesn't match above, return as-is
      if (typeof val === 'string' && val.includes('-')) return val;
      // Excel serial number (days since 1899-12-30)
      const num = Number(val);
      if (!isNaN(num) && num > 10000 && num < 100000) {
        const date = new Date(Math.round((num - 25569) * 86400 * 1000));
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
      }
      return String(val);
    };

    // Helper: Convert M/F to male/female
    const normalizeGender = (val) => {
      if (!val) return 'female';
      const v = String(val).trim().toLowerCase();
      if (v === 'm' || v === 'male') return 'male';
      if (v === 'f' || v === 'female') return 'female';
      return 'other';
    };

    const studentsToImport = data.map((row, idx) => {
      // Helper to handle variations in column names (Excel vs previous template)
      const getVal = (keys) => {
        for (const k of keys) {
          if (row[k] !== undefined && row[k] !== null && row[k] !== '') return row[k];
        }
        return null;
      };

      const statusVal = getVal(['Present Student = 0\r\nor\r\nPass out Student = 1', 'Present Student = 0\nor\nPass out Student = 1', 'Status']);
      const dob = excelDateToString(getVal(['Date of Birth', 'dateOfBirth']));
      const gender = normalizeGender(getVal(['Gender', 'gender']));
      const grNo = String(getVal(['GR No', 'grBookId']) || '');
      const admDate = excelDateToString(getVal(['Admission Date', 'admissionDate']));
      
      // Generate a default password: use DOB digits or fallback to 'password123'
      let password = 'password123';
      if (dob) {
        password = dob.replace(/-/g, ''); // e.g. "20100101"
      }

      // Generate a unique pseudo-email so the unique constraint doesn't block bulk insert of null emails
      const firstName = getVal(['First Name', 'firstName']) || 'student';
      const lastName = getVal(['Last Name', 'lastName']) || 'user';
      const ts = Date.now() + idx; // unique per student
      const pseudoEmail = `${firstName.toLowerCase().replace(/\s+/g, '')}.${lastName.toLowerCase().replace(/\s+/g, '')}.${grNo || idx}.${ts}@student.local`;

      // Ensure admissionNumber is unique
      const rollNo = String(getVal(['Roll No', 'rollNumber']) || idx);
      const admissionNumber = grNo ? `${grNo}` : `IMP-${ts}-${idx}-${rollNo}`;

      return {
        userData: {
          firstName: firstName,
          middleName: getVal(['Middle Name', 'middleName']),
          lastName: lastName,
          email: pseudoEmail,
          phone: String(getVal(['phone', 'Phone']) || ''),
          gender: gender,
          dateOfBirth: dob,
          addressStreet: getVal(['Permanent  Address', 'Permanent Address', 'address', 'Address']),
          addressCity: getVal(['city', 'City']),
          password: password
        },
        studentData: {
          admissionNumber: admissionNumber,
          grBookId: grNo,
          rollNumber: String(getVal(['Roll No', 'rollNumber']) || ''),
          classId: getVal(['classId']) || filterClass,
          sectionId: getVal(['sectionId']) || filterSection,
          academicYearId: currentAcademicYearId,
          fatherName: getVal(['initial Name', 'fatherName', 'Father Name']),
          motherName: getVal(['Mother Name', 'motherName']),
          uid: String(getVal(['Adhar Card no.', 'aadharCardNo', 'UID']) || ''),
          religion: getVal(['Religion ', 'Religion', 'religion']),
          caste: getVal(['Caste  with Sub-caste', 'Caste with Sub-caste', 'caste']),
          nationality: getVal(['Nationality', 'nationality']),
          motherTongue: getVal(['Mother Tongue', 'motherTongue']),
          admissionDate: admDate,
          status: statusVal == 1 || statusVal == '1' ? 'graduated' : 'active',
          bankName: getVal(['Bank name', 'bankName']),
          bankBranch: getVal(['Branch Name', 'bankBranch']),
          bankAccountNo: String(getVal(['Account No', 'bankAccountNo']) || ''),
          bankIfscCode: String(getVal(['IFSC No', 'bankIfscCode']) || ''),
          category: getVal(['Category', 'category']),
          regNo: getVal(['Registration No', 'regNo']),
          grBookName: getVal(['GR Book Name', 'grBookName']),
          apaarId: getVal(['APAAR ID', 'apaarId']),
          saralId: getVal(['SARAL ID', 'saralId']),
          enrollmentNumber: getVal(['Enrollment Number', 'enrollmentNumber'])
        }
      };
    });

    try {
      const res = await studentAPI.bulkImport({ students: studentsToImport });
      toast.success(res.data.message);
      fetchStudents();
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Import failed');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'inactive':
        return 'default';
      case 'graduated':
        return 'primary';
      case 'suspended':
        return 'error';
      default:
        return 'default';
    }
  };

  // Search filter
  const filteredStudents = students.filter(student => {
    if (!search) return true;
    const term = search.toLowerCase();
    const firstName = student.user?.firstName?.toLowerCase() || '';
    const middleName = student.user?.middleName?.toLowerCase() || '';
    const lastName = student.user?.lastName?.toLowerCase() || '';
    const admNo = student.admissionNumber?.toLowerCase() || '';
    const grNo = student.grBookId?.toLowerCase() || '';
    return firstName.includes(term) || middleName.includes(term) || lastName.includes(term) || admNo.includes(term) || grNo.includes(term);
  });

  return (
    <Box>
      {/* Modern Header */}
      <Box sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3,
        background: 'linear-gradient(135deg, #1a2332 0%, #2c3e50 60%, #1a9e8f 100%)',
        borderRadius: 3, p: 3, color: '#fff',
        position: 'relative', overflow: 'hidden',
        '&::before': {
          content: '""', position: 'absolute', top: -30, right: -30,
          width: 120, height: 120, borderRadius: '50%',
          background: 'rgba(255,255,255,0.06)',
        }
      }}>
        <Box>
          <Typography variant="h5" fontWeight={800} sx={{ color: '#fff !important', letterSpacing: -0.3 }}>
            Students Management
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6) !important', mt: 0.5 }}>
            Manage student records, class-wise selection, and import/export data
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          {user?.role !== 'teacher' && (
            <Button
              variant="outlined"
              startIcon={<ImportIcon />}
              onClick={() => setOpenImport(true)}
              sx={{
                color: '#fff !important', borderColor: 'rgba(255,255,255,0.3) !important',
                '&:hover': { borderColor: '#fff !important', background: 'rgba(255,255,255,0.1) !important' }
              }}
            >
              Import
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<ExportIcon />}
            onClick={exportToExcel}
            sx={{
              color: '#4caf50 !important', borderColor: 'rgba(76,175,80,0.5) !important',
              '&:hover': { borderColor: '#4caf50 !important', background: 'rgba(76,175,80,0.1) !important' }
            }}
          >
            Export
          </Button>
          {user?.role !== 'teacher' && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenForm()}
              sx={{
                background: 'linear-gradient(135deg, #4caf50 0%, #2e7d32 100%) !important',
                borderRadius: '10px !important',
                fontWeight: 700,
                px: 3,
                boxShadow: '0 4px 12px rgba(76,175,80,0.3) !important',
                '&:hover': {
                  boxShadow: '0 6px 20px rgba(76,175,80,0.4) !important',
                  transform: 'translateY(-2px) !important',
                }
              }}
            >
              Add Student
            </Button>
          )}
        </Box>
      </Box>

      {/* Filter Bar */}
      <Paper sx={{
        p: 2, mb: 3,
        borderRadius: '12px !important',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04) !important',
      }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by name, GR no..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#999' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px !important',
                  '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
                }
              }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              select
              fullWidth
              size="small"
              label="Select Class"
              value={filterClass}
              onChange={handleClassFilterChange}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px !important' } }}
            >
              <MenuItem value="">All Classes</MenuItem>
              {classes.filter(cls => user?.role !== 'teacher' || teacherAssignments.some(a => a.classId === cls.id)).map((cls) => (
                <MenuItem key={cls.id} value={cls.id}>{cls.name}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              select
              fullWidth
              size="small"
              label="Select Division"
              value={filterSection}
              onChange={(e) => setFilterSection(e.target.value)}
              disabled={!filterClass}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px !important' } }}
            >
              <MenuItem value="">All Sections</MenuItem>
              {sections.filter(sec => user?.role !== 'teacher' || teacherAssignments.some(a => a.classId === filterClass && (!a.sectionId || a.sectionId === sec.id))).map((sec) => (
                <MenuItem key={sec.id} value={sec.id}>{sec.name}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="column-visibility-label">Columns</InputLabel>
              <Select
                labelId="column-visibility-label"
                id="column-visibility-checkbox"
                multiple
                value={visibleColumns}
                onChange={handleColumnToggle}
                input={<OutlinedInput label="Columns" sx={{ borderRadius: '10px !important' }} />}
                renderValue={(selected) => `${selected.length} visible`}
              >
                {AVAILABLE_COLUMNS.map((col) => (
                  <MenuItem key={col.id} value={col.id}>
                    <Checkbox checked={visibleColumns.indexOf(col.id) > -1} />
                    <ListItemText primary={col.label} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper} sx={{
        mb: 2, overflowX: 'auto',
        borderRadius: '12px !important',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06) !important',
      }}>
        <Table sx={{ minWidth: 1000 }}>
          <TableHead>
            <TableRow sx={{
              background: 'linear-gradient(135deg, #1a2332 0%, #1a9e8f 100%) !important',
            }}>
              {visibleColumns.includes('grNo') && <TableCell sx={{ color: '#fff !important', fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: 'none !important' }}>GR No.</TableCell>}
              {visibleColumns.includes('libraryNumber') && <TableCell sx={{ color: '#fff !important', fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: 'none !important' }}>Library No.</TableCell>}
              {visibleColumns.includes('name') && <TableCell sx={{ color: '#fff !important', fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: 'none !important' }}>Name</TableCell>}
              {visibleColumns.includes('classDiv') && <TableCell sx={{ color: '#fff !important', fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: 'none !important' }}>Class & Div</TableCell>}
              {visibleColumns.includes('rollNo') && <TableCell sx={{ color: '#fff !important', fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: 'none !important' }}>Roll No.</TableCell>}
              {visibleColumns.includes('dob') && <TableCell sx={{ color: '#fff !important', fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: 'none !important' }}>Date of Birth</TableCell>}
              {visibleColumns.includes('gender') && <TableCell sx={{ color: '#fff !important', fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: 'none !important' }}>Gender</TableCell>}
              {visibleColumns.includes('uid') && <TableCell sx={{ color: '#fff !important', fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: 'none !important' }}>Aadhar/UID</TableCell>}
              {visibleColumns.includes('religion') && <TableCell sx={{ color: '#fff !important', fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: 'none !important' }}>Religion</TableCell>}
              {visibleColumns.includes('phone') && <TableCell sx={{ color: '#fff !important', fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: 'none !important' }}>Phone</TableCell>}
              {visibleColumns.includes('status') && <TableCell sx={{ color: '#fff !important', fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: 'none !important' }}>Status</TableCell>}
              <TableCell sx={{ color: '#fff !important', fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: 'none !important', minWidth: 120 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredStudents.length === 0 ? (
              <TableRow>
                <TableCell colSpan={11} align="center">
                  <Box sx={{ py: 5, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h6" color="text.secondary" fontWeight={600}>No students found</Typography>
                    <Typography variant="body2" color="text.secondary">Select a class or adjust your filters</Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              filteredStudents.map((student) => (
                <TableRow key={student.id} hover sx={{
                  transition: 'background 0.2s ease',
                  '&:hover': { background: 'rgba(26,158,143,0.04) !important' }
                }}>
                  {visibleColumns.includes('grNo') && <TableCell sx={{ fontWeight: 600, color: '#1a9e8f !important' }}>{student.grBookId || student.admissionNumber}</TableCell>}
                  {visibleColumns.includes('libraryNumber') && <TableCell sx={{ fontWeight: 600 }}>{student.libraryNumber || 'N/A'}</TableCell>}
                  
                  {visibleColumns.includes('name') && (
                  <TableCell>
                    <Typography variant="body2" fontWeight={600} sx={{ color: '#1a2332 !important' }}>
                      {student.user?.firstName} {student.user?.middleName} {student.user?.lastName}
                    </Typography>
                  </TableCell>
                  )}
                  
                  {visibleColumns.includes('classDiv') && <TableCell><Chip label={`${student.class?.name} - ${student.section?.name}`} size="small" sx={{ fontWeight: 600, bgcolor: '#e3f2fd !important', color: '#1565c0 !important' }} /></TableCell>}
                  {visibleColumns.includes('rollNo') && <TableCell sx={{ fontWeight: 600 }}>{student.rollNumber}</TableCell>}
                  {visibleColumns.includes('dob') && <TableCell>{student.user?.dateOfBirth ? new Date(student.user.dateOfBirth).toLocaleDateString() : 'N/A'}</TableCell>}
                  
                  {visibleColumns.includes('gender') && <TableCell>{student.user?.gender}</TableCell>}
                  {visibleColumns.includes('uid') && <TableCell>{student.uid || 'N/A'}</TableCell>}
                  {visibleColumns.includes('religion') && <TableCell>{student.religion || 'N/A'}</TableCell>}
                  
                  {visibleColumns.includes('phone') && <TableCell>{student.user?.phone || 'N/A'}</TableCell>}
                  
                  {visibleColumns.includes('status') && (
                  <TableCell>
                    <Chip
                      label={student.status}
                      color={getStatusColor(student.status)}
                      size="small"
                      sx={{ fontWeight: 600, borderRadius: '6px !important' }}
                    />
                  </TableCell>
                  )}
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton size="small" sx={{
                        color: '#1a9e8f !important',
                        '&:hover': { background: 'rgba(26,158,143,0.1) !important' }
                      }}>
                        <ViewIcon fontSize="small" />
                      </IconButton>
                      {(user?.role !== 'teacher' || (isClassTeacher && classTeacherOf?.class?.id === student.classId && (!classTeacherOf?.section?.id || classTeacherOf?.section?.id === student.sectionId))) && (
                        <IconButton size="small" sx={{
                          color: '#2196f3 !important',
                          '&:hover': { background: 'rgba(33,150,243,0.1) !important' }
                        }} onClick={() => handleOpenForm(student)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      )}
                      {user?.role !== 'teacher' && (
                        <IconButton size="small" sx={{
                          color: '#e53935 !important',
                          '&:hover': { background: 'rgba(229,57,53,0.1) !important' }
                        }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <StudentForm
        open={openForm}
        handleClose={handleCloseForm}
        studentToEdit={studentToEdit}
        refreshData={fetchStudents}
      />

      <ExcelImport
        open={openImport}
        onClose={() => setOpenImport(false)}
        onImport={handleImportStudents}
        title="Import Students"
        template={{
          "Roll No": "1",
          "initial Name": "Rajendra",
          "Last Name": "Doe",
          "First Name": "John",
          "Middle Name": "R",
          "Mother Name": "Jane",
          "Religion ": "Hindu",
          "Caste  with Sub-caste": "Maratha",
          "Nationality": "Indian",
          "Birth Place": "Pune",
          "Mother Tongue": "Marathi",
          "Date of Birth": "2010-01-01",
          "Last School Name": "ABC School",
          "Admission Date": "2020-06-15",
          "Admited Standard": "1st",
          "Progress": "Good",
          "Conduct": "Good",
          "Date of Leaving School": "",
          "Tc given Date": "",
          "Left standard": "",
          "from which date": "",
          "Reason for Leaving School": "",
          "Remarks": "",
          "TC No": "",
          "Gender": "Male",
          "Present Student = 0\r\nor\r\nPass out Student = 1": "0",
          "Permanent  Address": "123 Main St, Pune",
          "Parent occupation": "Farmer",
          "Concession": "",
          "Category": "OPEN",
          "Minority": "No",
          "Education Officer no": "",
          "Adhar Card no.": "123412341234",
          "Bank name": "SBI",
          "Branch Name": "Pune",
          "Account No": "123456789",
          "IFSC No": "SBIN0001234",
          "GR No": "945",
          "Registration No": "REG123",
          "GR Book Name": "GR Book A",
          "APAAR ID": "123456789012",
          "SARAL ID": "SAR123",
          "Enrollment Number": "ENR123"
        }}
      />
    </Box>
  );
};

export default Students;
